import { Button } from "@car/ui/components/button";
import { Calendar } from "@car/ui/components/calendar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@car/ui/components/card";
import { statusControlClass, useAppForm } from "@car/ui/components/form";
import { Input } from "@car/ui/components/input";
import { Label } from "@car/ui/components/label";
import { Popover, PopoverContent, PopoverTrigger } from "@car/ui/components/popover";
import { Skeleton } from "@car/ui/components/skeleton";
import { emailText, nationalIdText, optionalText, requiredText } from "@car/ui/lib/form-rules";
import { cn } from "@car/ui/lib/utils";
import { Calendar03Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { z } from "zod";

import { MyDataCard, PhoneChangeDialog, SessionsCard } from "@/components/panel-account";
import { PageHeader, PanelQueryError } from "@/components/panel-bits";
import { faDate, toEnDigits, toFaDigits } from "@/lib/format";
import type { Profile } from "@/lib/panel";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/panel/profile")({
  head: () => ({ meta: [{ title: "پروفایل | پنل مشتریان تابان خودرو" }] }),
  component: ProfilePage,
});

function ProfilePage() {
  const query = useQuery(orpc.panel.profile.get.queryOptions());

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader
        title="پروفایل"
        description="اطلاعات حساب، شماره موبایل ورود، دستگاه‌ها و اطلاعات شما."
      />
      {query.isPending ? (
        <Skeleton className="h-96 rounded-xl" />
      ) : query.isError ? (
        <PanelQueryError title="اطلاعات حساب بارگیری نشد" query={query} />
      ) : (
        <div className="grid gap-6">
          <ProfileForm profile={query.data.profile} locked={query.data.identityLocked} />
          <SessionsCard />
          <MyDataCard deletionNumber={query.data.deletionRequest?.number ?? null} />
        </div>
      )}
    </div>
  );
}

const nationalIdRule = nationalIdText({ optional: true });

// سقف طول‌ها همان سقف سرور است. کد ملیِ ثبت‌شده (حتی کدی که پیش از قاعده رقم کنترل ذخیره شده)
// تا وقتی عوض نشود مانع ذخیره بقیه فیلدها نیست؛ سرور هم همین را می‌پذیرد
const profileSchemaFor = (storedId: string) =>
  z.object({
    firstName: requiredText("نام", { max: 60 }),
    lastName: requiredText("نام خانوادگی", { max: 60 }),
    nationalId: z.string().superRefine((value, context) => {
      if (storedId !== "" && toEnDigits(value.trim()) === toEnDigits(storedId)) return;
      for (const issue of nationalIdRule.safeParse(value).error?.issues ?? []) {
        context.addIssue({ code: "custom", message: issue.message });
      }
    }),
    email: emailText({ optional: true }),
    city: optionalText("شهر", 60),
    birthDateIso: z.string(),
  });

/** `locked`: name and national id are on a purchase, order or contract; only an empty one is editable */
function ProfileForm({ profile, locked }: { profile: Profile; locked: boolean }) {
  const queryClient = useQueryClient();
  const nameLocked = locked && `${profile.firstName}${profile.lastName}`.trim() !== "";
  const idLocked = locked && profile.nationalId !== "";

  const mutation = useMutation(
    orpc.panel.profile.update.mutationOptions({
      onSuccess: (result) => {
        if (!result.ok) {
          toast.error(result.reason);
          return;
        }
        toast.success("پروفایل شما به‌روزرسانی شد");
        queryClient.invalidateQueries();
      },
    }),
  );

  const form = useAppForm({
    defaultValues: {
      firstName: profile.firstName,
      lastName: profile.lastName,
      nationalId: profile.nationalId,
      email: profile.email,
      city: profile.city,
      birthDateIso: profile.birthDateIso,
    },
    validators: { onBlur: profileSchemaFor(profile.nationalId) },
    onSubmit: async ({ value }) => {
      await mutation
        .mutateAsync({
          firstName: value.firstName.trim(),
          lastName: value.lastName.trim(),
          nationalId: toEnDigits(value.nationalId.trim()),
          email: value.email.trim(),
          birthDateIso: value.birthDateIso,
          city: value.city.trim(),
        })
        .catch(() => undefined);
    },
  });

  return (
    <form.AppForm>
      <form.Form>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">اطلاعات فردی</CardTitle>
            <CardDescription>
              این اطلاعات برای صدور فاکتور و اطلاع‌رسانی خدمات استفاده می‌شود.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <form.AppField name="firstName">
                {(field) => (
                  <field.TextField label="نام" autoComplete="given-name" readOnly={nameLocked} />
                )}
              </form.AppField>
              <form.AppField name="lastName">
                {(field) => (
                  <field.TextField
                    label="نام خانوادگی"
                    autoComplete="family-name"
                    readOnly={nameLocked}
                  />
                )}
              </form.AppField>
            </div>
            {nameLocked || idLocked ? (
              <p className="text-xs text-muted-foreground">
                نام و کد ملی شما در خرید، سفارش یا قرارداد ثبت شده است؛ برای اصلاح آن‌ها{" "}
                <Link
                  to="/panel/tickets"
                  className="font-bold text-primary underline-offset-4 hover:underline"
                >
                  تیکت پشتیبانی
                </Link>{" "}
                ثبت کنید یا با پشتیبانی تماس بگیرید.
              </p>
            ) : null}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor="phone">شماره موبایل</Label>
                {/* form-kit-exempt: نمایش فقط‌خواندنی شماره ورود، جزو مقدارهای فرم نیست */}
                <Input
                  id="phone"
                  dir="ltr"
                  readOnly
                  disabled
                  value={toFaDigits(profile.phone)}
                  className="text-start"
                />
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs text-muted-foreground">شماره ورود و پیامک‌ها</p>
                  <PhoneChangeDialog currentPhone={profile.phone} />
                </div>
              </div>
              <form.AppField name="nationalId">
                {(field) => (
                  <field.TextField
                    label="کد ملی (اختیاری)"
                    dir="ltr"
                    inputMode="numeric"
                    className="text-start"
                    readOnly={idLocked}
                  />
                )}
              </form.AppField>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <form.AppField name="email">
                {(field) => (
                  <field.TextField
                    label="ایمیل (اختیاری)"
                    dir="ltr"
                    type="email"
                    autoComplete="email"
                    className="text-start"
                  />
                )}
              </form.AppField>
              <form.AppField name="city">
                {(field) => <field.TextField label="شهر (اختیاری)" autoComplete="address-level2" />}
              </form.AppField>
            </div>

            <form.AppField name="birthDateIso">
              {(field) => (
                <field.FormField label="تاریخ تولد (اختیاری)">
                  {({ status, controlProps }) => (
                    <Popover
                      onOpenChange={(open) => {
                        if (!open) field.handleBlur();
                      }}
                    >
                      <PopoverTrigger
                        render={
                          <Button
                            type="button"
                            variant="outline"
                            id={controlProps.id}
                            aria-invalid={controlProps["aria-invalid"]}
                            aria-describedby={controlProps["aria-describedby"]}
                            className={cn("justify-between", statusControlClass(status))}
                          />
                        }
                      >
                        {field.state.value ? faDate(field.state.value) : "انتخاب تاریخ"}
                        <HugeiconsIcon icon={Calendar03Icon} strokeWidth={1.8} />
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" lang="fa" dir="rtl">
                        <Calendar
                          mode="single"
                          captionLayout="dropdown"
                          selected={field.state.value ? new Date(field.state.value) : undefined}
                          onSelect={(next) => {
                            field.handleChange(next ? next.toISOString() : "");
                            field.handleBlur();
                          }}
                          disabled={(day) => day > new Date()}
                          endMonth={new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                  )}
                </field.FormField>
              )}
            </form.AppField>

            <div className="flex justify-end">
              <form.SubmitButton pending={mutation.isPending}>ذخیره تغییرات</form.SubmitButton>
            </div>
          </CardContent>
        </Card>
      </form.Form>
    </form.AppForm>
  );
}
